import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Typography,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, People, ContentCopy } from '@mui/icons-material';
import adminService, { AdminUserRecord, City } from '../../services/adminService';
import { PageHeader, ResponsiveTable, StatCard, StatusChip } from '../../components/common';

const AdminUsers: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUserRecord[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [cityId, setCityId] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdCredential, setCreatedCredential] = useState<{ email: string; tempPassword: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const cityName = (id: string | null) => cities.find((c) => c.id === id)?.name || (id ? id : '—');

  const load = async () => {
    setLoading(true);
    try {
      const [adminList, cityList] = await Promise.all([
        adminService.getAllAdminUsers(),
        adminService.getAllCities(),
      ]);
      setAdmins(adminList);
      setCities(cityList);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEmail('');
    setName('');
    setCityId(cities.find((c) => c.isActive)?.id || '');
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!email.trim() || !name.trim() || !cityId) return;
    setSaving(true);
    try {
      const result = await adminService.createAdminUser(email.trim(), name.trim(), cityId);
      setDialogOpen(false);
      setCreatedCredential({ email: email.trim(), tempPassword: result.tempPassword });
      await load();
    } catch (error: any) {
      setSnackbar({ message: error?.message || 'Failed to create admin', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (admin: AdminUserRecord) => {
    try {
      await adminService.setAdminUserActive(admin.uid, !admin.isActive);
      await load();
    } catch (error: any) {
      setSnackbar({ message: error?.message || 'Failed to update admin', severity: 'error' });
    }
  };

  return (
    <Box>
      <PageHeader
        title="Admin Users"
        subtitle="City admins and managers — each scoped to exactly one city"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={cities.length === 0}>
            Add City Admin
          </Button>
        }
      />

      {cities.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Add a city first — a city admin has to be assigned to one.
        </Alert>
      )}

      <Box sx={{ mb: 3, maxWidth: 220 }}>
        <StatCard label="Total Admins" value={admins.length} icon={<People />} tone="primary" />
      </Box>

      <ResponsiveTable<AdminUserRecord>
        rows={admins}
        rowKey={(a) => a.uid}
        loading={loading}
        emptyTitle="No admin users yet"
        aria-label="Admin users"
        columns={[
          { key: 'name', header: 'Name', mobile: 'title', render: (a) => a.name },
          { key: 'email', header: 'Email', mobile: 'subtitle', render: (a) => a.email },
          {
            key: 'role',
            header: 'Role',
            mobile: 'body',
            render: (a) => (a.role === 'super_admin' ? 'Super Admin' : 'City Admin'),
          },
          { key: 'city', header: 'City', mobile: 'body', render: (a) => cityName(a.cityId) },
          {
            key: 'status',
            header: 'Status',
            mobile: 'body',
            render: (a) => <StatusChip status={a.isActive ? 'active' : 'inactive'} />,
          },
          {
            key: 'actions',
            header: 'Actions',
            mobile: 'actions',
            render: (a) =>
              a.role === 'city_admin' ? (
                <Button size="small" color={a.isActive ? 'error' : 'success'} onClick={() => handleToggleActive(a)}>
                  {a.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              ),
          },
        ]}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add City Admin</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField select fullWidth label="City" value={cityId} onChange={(e) => setCityId(e.target.value)}>
            {cities
              .filter((c) => c.isActive)
              .map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}, {c.state}
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !email.trim() || !name.trim() || !cityId}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-time credential display — this is the only place the temporary
          password is ever shown, since nothing here sends email. */}
      <Dialog open={!!createdCredential} onClose={() => setCreatedCredential(null)} maxWidth="xs" fullWidth>
        <DialogTitle>City Admin Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This password is shown once. Share it securely — it won't be shown again.
          </Alert>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {createdCredential?.email}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Temporary Password
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                  {createdCredential?.tempPassword}
                </Typography>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (createdCredential) navigator.clipboard.writeText(createdCredential.tempPassword);
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedCredential(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity}>{snackbar.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
